/** Vendors */
import axios from "axios";

/** Enums */
import { Provider } from "../types";

/** Types */
import type { IOAuthTokenResponse } from "../types";

/** Environmentals */
const app = import.meta.env.VITE_REACT_APP_BU_APP_RESOURCE_NAME;
const client = import.meta.env.VITE_REACT_APP_BU_CLIENT_RESOURCE_NAME;

/** Auth API */
export function createInstance() {
  /** Step . Create instance of axios to remove auth headers for upload. */
  const instance = axios.create({
    baseURL: String(import.meta.env.VITE_REACT_APP_API_ENDPOINT),
  });

  instance.defaults.headers.common["Accept"] = "application/json";
  // instance.defaults.headers.common["Authorization"] = `Bearer ${authorization}`;
  instance.defaults.headers.common["Content-Type"] = "application/json";
  // instance.defaults.headers.common["x-api-key"] = api_key;
  instance.defaults.headers.common["x-bu-app"] = app;
  instance.defaults.headers.common["x-bu-client"] = client;

  instance.interceptors.response.use(
    function (response) {
      if (!response.hasOwnProperty("data")) return response;
      return response.data;
    },
    function (error) {
      console.log(error);
      if (
        error.message &&
        (error.response.message === "The incoming token has expired" ||
          error.response.data.message === "The incoming token has expired")
      ) {
        location.reload();
      }

      return Promise.reject({
        response: error.response.message.data.message,
        description: error.response.message.data.error_description,
      });
    }
  );

  instance.interceptors.response.use(
    function (response) {
      if (!Object.prototype.hasOwnProperty.call(response, "data")) {
        return response;
      }
      return response.data;
    },
    function (error) {
      console.log("Error Message: ", error?.message);
      return Promise.reject({ error: error.message });
    }
  );

  return instance;
}

export async function createVisitorToken(): Promise<IOAuthTokenResponse> {
  const instance = createInstance();
  try {
    /** Step . See if sessionStorage has an existing token */
    const credentials = sessionStorage.getItem("@BU_AUTH");
    if (!credentials) throw new Error("No Active Session");
    const token = JSON.parse(credentials);

    if (!token?.access_token) {
      throw new Error(
        "Missing Auth Token. Reinitialize Token Issuance Process Flow"
      );
    }

    /** Step . Verify the token */
    await instance.get("/oauth/verify", {
      headers: {
        Authorization: `Bearer ${token.access_token}`,
        "x-api-key": token.api_key,
      },
    });

    /** Step . Set the axios headers for future requests */
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token.access_token}`;

    axios.defaults.headers.common["x-api-key"] = token.api_key;

    return token;
  } catch (error) {
    instance.defaults.headers.common["Authorization"] = null;
    instance.defaults.headers.common["x-api-key"] = null;

    const authorize = await instance.get("/oauth/authorize", {
      params: {
        app,
        client,
        callback_url: location.href,
        provider: Provider.BU,
      },
    });

    const callback = await instance.get("/oauth/callback", {
      params: authorize,
    });

    const token = await instance.post("/oauth/token", callback);

    /** Step . Set the axios headers for future requests */
    axios.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${token.access_token}`;

    axios.defaults.headers.common["x-api-key"] = token.api_key;

    /** Step . Store the tokens for future refresh use */
    sessionStorage.setItem("@BU_AUTH", JSON.stringify(token));

    return token;
  }
}

const auth = {
  create: {
    visitor: {
      token: createVisitorToken,
    },
  },
};

export default auth;
